import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { IPreflight, PreflightReason } from "../types/api/istio.type";

interface IExposureWarningDialog {
  open: boolean;
  preflight: IPreflight | null;
  gateway?: string;
  /** Create the environment anyway, without a virtual service. */
  onProceed: () => void;
  /** Create nothing at all — not even the logical environment. */
  onCancel: () => void;
}

const CopyBlock = ({ label, value }: { label: string; value: string }) => (
  <Box mt={1}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => navigator.clipboard?.writeText(value)}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        maxHeight: 180,
        overflow: "auto",
        fontSize: 12,
        bgcolor: "action.hover",
        borderRadius: 1,
      }}
    >
      {value}
    </Box>
  </Box>
);

/** Whether "proceed without" is offered: the other reasons are transient or fixable in the form. */
export const canProceedWithoutExposure = (reason?: PreflightReason | null) =>
  reason === "ports_exhausted" || reason === "rbac_denied" || reason === "capacity_unknown";

/**
 * Shown when preflight says an exposure is not possible. Only `ports_exhausted` offers
 * "proceed without" — the other reasons are either transient or fixable in the form, and
 * creating an unexposed database for them would be the wrong outcome.
 */
export const ExposureWarningDialog = ({
  open,
  preflight,
  gateway,
  onProceed,
  onCancel,
}: IExposureWarningDialog) => {
  const [showRemediation, setShowRemediation] = useState(false);
  const reason = preflight?.reason;
  const remediation = preflight?.remediation;
  const canProceedWithout = canProceedWithoutExposure(reason);

  const gatewayName = gateway || "the database gateway";
  const title =
    reason === "ports_exhausted"
      ? "Warning: no Istio ports left"
      : reason === "ports_contended"
      ? "Ports are momentarily busy"
      : reason === "rbac_denied"
      ? "External access is unavailable"
      : reason === "capacity_unknown"
      ? "Could not check Istio port capacity"
      : "Cannot expose this environment";

  const body =
    reason === "ports_exhausted"
      ? `All ${preflight?.declared_count ?? 0} ports on ${gatewayName} are in use. Your database will be created without a virtual service, so it will not be reachable from outside the cluster. You can expose it later once more ports are added.`
      : reason === "ports_contended"
      ? "Another exposure is being created right now and is holding the remaining port(s). Nothing has been created. Try again in a few seconds."
      : reason === "capacity_unknown"
      ? "The port capacity check could not be reached, so it is not known whether a port is free. Creating the database without a virtual service is safe — you can expose it afterwards from the environment's details."
      : preflight?.message || "The environment cannot be exposed right now.";

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        <Alert severity={canProceedWithout ? "warning" : "info"} sx={{ mb: 2 }}>
          {body}
        </Alert>

        {canProceedWithout && (
          <DialogContentText>
            <strong>Proceed anyway?</strong>
          </DialogContentText>
        )}

        {remediation && (
          <Box mt={2}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => setShowRemediation((v) => !v)}
            >
              {showRemediation ? "Hide" : "How to add more ports"}
            </Link>
            <Collapse in={showRemediation}>
              <Typography variant="body2" mt={1}>
                In <strong>{remediation.repo}</strong>, edit{" "}
                <code>{remediation.file}</code> in <strong>both</strong> places, then commit to
                main. ArgoCD syncs automatically.
              </Typography>
              <CopyBlock
                label="istio-ingressgateway-internal.service.ports"
                value={remediation.suggested_yaml.service_ports}
              />
              <CopyBlock
                label="gateway.gateways[ingressgateway-internal-tsdb-gw].servers"
                value={remediation.suggested_yaml.gateway_servers}
              />
            </Collapse>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onCancel} sx={{ mr: 1 }}>
          {canProceedWithout ? "No, cancel" : "Close"}
        </Button>
        {canProceedWithout && (
          <Button variant="contained" color="warning" onClick={onProceed} sx={{ mr: 1 }}>
            Yes, create without it
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExposureWarningDialog;
